/**
 * AlertasPage — Alertas automáticas detectadas sobre agencias.
 * Acciones: marcar resuelta, abrir vista 360, ejecutar (registrar visita).
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Chip, Paper, Stack, FormControlLabel,
  Switch, TextField, IconButton,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventIcon from '@mui/icons-material/Event';
import RefreshIcon from '@mui/icons-material/Refresh';
import Agencia360 from './Agencia360';
import VisitaForm from './VisitaForm';
import {
  API_BASE, getEmpresaId, getAuthHeaders, fechaRelativa, getCurrentUser,
} from './format';

const TIPO_META = {
  inactividad_reciente:    { color: '#dc2626', icon: '⏸️', label: 'Inactividad reciente' },
  caida_fuerte:            { color: '#f59e0b', icon: '📉', label: 'Caída fuerte' },
  sin_contacto_comercial:  { color: '#8b5cf6', icon: '👋', label: 'Sin contacto' },
  deuda_alta:              { color: '#dc2626', icon: '💸', label: 'Deuda alta' },
};

const SEV_META = {
  alta:  { color: '#dc2626', label: 'Alta' },
  media: { color: '#f59e0b', label: 'Media' },
  baja:  { color: '#0ea5e9', label: 'Baja' },
};

export default function AlertasPage() {
  const me = getCurrentUser();
  const puedeDetectar = ['admin', 'superadmin', 'coordinador'].includes((me.role || '').toLowerCase());

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarResueltas, setMostrarResueltas] = useState(false);
  const [detectando, setDetectando] = useState(false);
  const [agencia360Id, setAgencia360Id] = useState(null);
  const [visitaPrefill, setVisitaPrefill] = useState(null);
  const [resolviendo, setResolviendo] = useState(null);  // { alertaId, notas }

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        empresa_id: getEmpresaId(),
        resueltas: String(mostrarResueltas),
      });
      const r = await fetch(`${API_BASE}/turismo/comercial/alertas?${params}`,
        { headers: getAuthHeaders() });
      const d = await r.json();
      setItems(d.alertas || []);
    } catch { setItems([]); }
    setLoading(false);
  }, [mostrarResueltas]);
  useEffect(() => { cargar(); }, [cargar]);

  const handleDetectar = async () => {
    setDetectando(true);
    try {
      await fetch(`${API_BASE}/turismo/comercial/alertas/detectar-ahora?empresa_id=${getEmpresaId()}`,
        { method: 'POST', headers: getAuthHeaders() });
      await cargar();
    } catch {}
    setDetectando(false);
  };

  const handleResolver = async (alertaId, notas) => {
    try {
      await fetch(`${API_BASE}/turismo/comercial/alertas/${alertaId}`, {
        method: 'PATCH', headers: getAuthHeaders(),
        body: JSON.stringify({ accion: 'resuelta', notas_resolucion: notas || null }),
      });
      setResolviendo(null);
      await cargar();
    } catch {}
  };

  const handleMarcarVista = async (alertaId) => {
    try {
      await fetch(`${API_BASE}/turismo/comercial/alertas/${alertaId}`, {
        method: 'PATCH', headers: getAuthHeaders(),
        body: JSON.stringify({ accion: 'vista' }),
      });
      await cargar();
    } catch {}
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Alertas</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Señales tempranas detectadas automáticamente sobre tus agencias.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Switch size="small" checked={mostrarResueltas}
            onChange={e => setMostrarResueltas(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 12 }}>Ver resueltas</Typography>}
        />
        {puedeDetectar && (
          <Button size="small" startIcon={<RefreshIcon />} disabled={detectando}
            onClick={handleDetectar}
            sx={{ textTransform: 'none', color: '#0d9488', fontWeight: 700 }}>
            {detectando ? 'Detectando…' : 'Detectar ahora'}
          </Button>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress sx={{ color: '#dc2626' }} /></Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#16a34a', mb: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {mostrarResueltas ? 'No hay alertas resueltas aún' : 'Sin alertas pendientes 🎉'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5 }}>
            {puedeDetectar
              ? 'El job corre cada día a las 8AM (Bogotá). Puedes detectar ahora si quieres ejecutarlo manualmente.'
              : 'Cuando se detecte una nueva alerta aparecerá aquí.'}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {items.map(a => {
            const meta = TIPO_META[a.tipo] || { color: '#64748b', icon: '⚠️', label: a.tipo };
            const sev = SEV_META[a.severidad] || SEV_META.media;
            return (
              <Paper key={a.id}
                sx={{
                  p: 1.5, borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  borderLeft: `4px solid ${sev.color}`,
                  opacity: a.resuelta_at ? 0.6 : 1,
                  bgcolor: a.vista_at ? '#fafafa' : '#fff',
                }}
                onMouseEnter={() => !a.vista_at && handleMarcarVista(a.id)}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                  <Box sx={{ fontSize: 28 }}>{meta.icon}</Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                        {a.agencia_nombre}
                      </Typography>
                      <Chip size="small" label={meta.label}
                        sx={{ height: 18, fontSize: 10, fontWeight: 700,
                               bgcolor: `${meta.color}20`, color: meta.color }} />
                      <Chip size="small" label={`Sev. ${sev.label}`}
                        sx={{ height: 18, fontSize: 9, fontWeight: 700,
                               bgcolor: `${sev.color}20`, color: sev.color }} />
                      {a.resuelta_at && (
                        <Chip size="small" label="Resuelta"
                          sx={{ height: 18, fontSize: 9, bgcolor: '#dcfce7', color: '#166534' }} />
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: sev.color, mb: 0.25 }}>
                      {a.titulo}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#475569' }}>
                      {a.mensaje}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.5 }}>
                      Detectada {fechaRelativa(a.detectada_at)}
                      {a.resuelta_at && ` · Resuelta ${fechaRelativa(a.resuelta_at)}`}
                    </Typography>
                    {a.notas_resolucion && (
                      <Typography sx={{ fontSize: 11, color: '#0d9488', fontStyle: 'italic', mt: 0.5 }}>
                        ✓ {a.notas_resolucion}
                      </Typography>
                    )}
                  </Box>
                  {!a.resuelta_at && (
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" startIcon={<VisibilityIcon />}
                        onClick={() => setAgencia360Id(a.agencia_id)}
                        sx={{ textTransform: 'none', fontSize: 11 }}>
                        Ver 360
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<EventIcon />}
                        onClick={() => setVisitaPrefill({
                          agencia_id: a.agencia_id,
                          agencia_nombre: a.agencia_nombre,
                        })}
                        sx={{ textTransform: 'none', fontSize: 11,
                              color: '#0d9488', borderColor: '#0d9488' }}>
                        Visitar
                      </Button>
                      <Button size="small" variant="contained" disableElevation
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={() => setResolviendo({ alertaId: a.id, notas: '' })}
                        sx={{ textTransform: 'none', fontSize: 11, bgcolor: '#16a34a',
                              '&:hover': { bgcolor: '#15803d' } }}>
                        Resolver
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Diálogo simple de resolución */}
      {resolviendo && (
        <Paper sx={{
          position: 'fixed', bottom: 16, left: 16, right: 16,
          maxWidth: 480, mx: 'auto', p: 2, borderRadius: 2, zIndex: 1300,
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
            ¿Qué hiciste para resolver esta alerta? (opcional)
          </Typography>
          <TextField fullWidth size="small" multiline minRows={2}
            placeholder="Ej. Llamé y agendamos visita para el martes"
            value={resolviendo.notas}
            onChange={e => setResolviendo({ ...resolviendo, notas: e.target.value })} />
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button onClick={() => setResolviendo(null)} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" disableElevation
              onClick={() => handleResolver(resolviendo.alertaId, resolviendo.notas)}
              sx={{ textTransform: 'none', bgcolor: '#16a34a',
                    '&:hover': { bgcolor: '#15803d' } }}>
              Marcar resuelta
            </Button>
          </Stack>
        </Paper>
      )}

      {agencia360Id && (
        <Agencia360 agenciaId={agencia360Id} onClose={() => setAgencia360Id(null)} />
      )}
      {visitaPrefill && (
        <VisitaForm open onClose={() => setVisitaPrefill(null)}
          prefill={visitaPrefill}
          onSaved={() => { setVisitaPrefill(null); cargar(); }} />
      )}
    </Box>
  );
}
